import { AgGridTable } from '@components/AgGrid/AgGridTable';
import type { CatalogRatioDataForList, CatalogRatioGroupData, CatalogRatioShortData } from '@gql/graphql';
import { DismantlingCoefficientTypeEnum, RatioTypeEnum } from '@gql/graphql';

import type React from 'react';
import { useCallback, useMemo } from 'react';

import { defaultColDefSet } from '../../constants';
import { getRowClass, getRowId } from '../../helpers';
import { useMassApplicationRatiosColumns, useMassApplicationRatiosOutputData } from '../../hooks';
import Styles from '../../massRatioApplication.module.css';
import type { TRatioTypeSelectItem, TTableFiltersProps } from '../../types';

type TSelectRatiosStepProps = {
    selectedRatioType: TRatioTypeSelectItem | null;
    selectedRatioGroup: CatalogRatioGroupData | null;
    checkedRatios: Record<string, CatalogRatioShortData | null>;
    setCheckedRatios: React.Dispatch<React.SetStateAction<Record<string, CatalogRatioShortData | null>>>;
    tableUniqueId: string;
    responseRatioType?: RatioTypeEnum;
    responseRatioGroupId?: string | number;
    changeFilterHandler: (filter: TTableFiltersProps) => void;
    ratiosData: CatalogRatioDataForList[];
    subGroupsNames: string[];
    ratioGroupIds: string[];
};

export const SelectRatiosStep = ({
    selectedRatioType,
    selectedRatioGroup,
    checkedRatios,
    setCheckedRatios,
    tableUniqueId,
    responseRatioType,
    responseRatioGroupId,
    changeFilterHandler,
    ratiosData,
    subGroupsNames,
    ratioGroupIds,
}: TSelectRatiosStepProps) => {
    const isConstructionWorksGroupSelected = useMemo(
        () =>
            selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks &&
            !selectedRatioGroup?.parentId,
        [selectedRatioGroup?.groupType, selectedRatioGroup?.parentId],
    );

    const sortColDef = useMemo(() => {
        if (selectedRatioType?.id === RatioTypeEnum.TechPartCoef) {
            return responseRatioType ? defaultColDefSet : {};
        }

        return !!responseRatioType && !!responseRatioGroupId ? defaultColDefSet : {};
    }, [selectedRatioType, responseRatioGroupId, responseRatioType]);

    const overlayNoRowsTemplate = (() => {
        if (!selectedRatioType?.id) {
            return 'Для отображения данных выберите тип коэффициента';
        }
        if (selectedRatioType?.id !== RatioTypeEnum.TechPartCoef && !selectedRatioGroup?.id) {
            return 'Для отображения данных выберите раздел';
        }

        return 'Нет данных';
    })();

    const handleChangeCheckbox = useCallback(
        (row: CatalogRatioShortData) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedRatios((ids) => ({
                    ...ids,
                    [row.id]: checked ? row : null,
                }));
            },
        [setCheckedRatios],
    );

    const { currentRatiosRowData } = useMassApplicationRatiosOutputData({
        selectedRatioGroup,
        responseRatioGroupId,
        responseRatioType,
        ratiosData,
    });

    const getCanCheckRow = useCallback(
        (row: CatalogRatioShortData) => {
            if (
                ratioGroupIds.length &&
                selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks
            ) {
                return ratioGroupIds[0] === String(row.catalogRatioGroupId);
            }

            return true;
        },
        [ratioGroupIds, selectedRatioGroup?.groupType],
    );

    const { columns: ratioColumns } = useMassApplicationRatiosColumns({
        data: currentRatiosRowData,
        checkedIds: checkedRatios,
        changeFilterHandler,
        handleChangeCheckbox,
        ratioType: selectedRatioType?.id,
        ratioGroupId: selectedRatioGroup?.id,
        subGroupsNames,
        isConstructionWorksGroupSelected,
        sortColDef,
        getCanCheckRow,
    });

    return (
        <div id="addCoefficient" className={`AddCoefficient ${Styles.ratioApplicationTable}`}>
            <AgGridTable
                getRowId={getRowId}
                rowData={currentRatiosRowData}
                columnDefs={ratioColumns}
                rowClassRules={getRowClass}
                rowSelection="multiple"
                rowHeight={40}
                key={tableUniqueId}
                context={{ tableUniqueId }}
                overlayNoRowsTemplate={overlayNoRowsTemplate}
            />
        </div>
    );
};

SelectRatiosStep.displayName = 'SelectRatiosStep';
